import { useEffect, useId, useState } from "react";
import { PlusIcon, XIcon } from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import { SKELETON_PIECE_CLASS } from "@/components/molecules/SelectableListItemRow";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { Mutations, Queries } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useEnumColor } from "@/hooks/useEnumColor";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { Utils } from "@/utils";
import { useBudgetViewStore } from "./BudgetView.store";

import {
  type BudgetViewBudget,
  type BudgetViewLineMonth,
  nameBudgetLine,
} from "./BudgetViewMonth";
import { Input } from "@/components/ui/input";

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const KINDS = [
  { value: "BILL", label: "Bills", hint: "paid each month, about the same" },
  {
    value: "EVERYDAY",
    label: "Everyday",
    hint: "spent as the month goes; starts afresh each month, unless it is spread over months",
  },
  {
    value: "SAVING",
    label: "Saving for",
    hint: "put by each month towards a goal",
  },
  { value: "DEBT", label: "Paying off", hint: "paid off each month" },
] as const;

/** A part of the plan: the theme's surface, framed and lifted a little. */
const SECTION_CLASS =
  "rounded-lg border border-border bg-card shadow-xs p-3 flex flex-col gap-2";

/**
 * The plan every month is held to: what is expected to come in, and what each
 * line gets - bills, everyday spending, what is put by, what is paid off - set
 * once and the same each month. It says whether the plan fits what comes in,
 * and how much of it no line has been given.
 */
export function BudgetViewPlan({
  budget,
  isLoading,
}: {
  /** The budget read - or, while it is read, a typical one drawn in skeletons. */
  budget: BudgetViewBudget;
  isLoading: boolean;
}) {
  const currency =
    budget.amountCurrency !== budget.entity.currency
      ? (budget.amountCurrency ?? undefined)
      : undefined;
  // Its lines once read: none drawn while it is.
  const rows = isLoading ? [] : budget.month.lines;
  const income = Number(budget.amountIncome);
  // What each line takes this month: a wish had by a day, what the months left need of it.
  const planned = (kinds: string[]) =>
    rows
      .filter((row) => kinds.includes(row.line.kind))
      .reduce((sum, row) => sum + Number(row.amountPlanned), 0);
  const committed = planned(["BILL", "SAVING", "DEBT"]);
  const everyday = planned(["EVERYDAY"]);
  const unplanned = income - committed - everyday;
  const money = (amount: number | string) =>
    Utils.fCurrency(amount, false, false, currency);
  const over = useEnumColor(EnumColors.RED);
  const setTab = useBudgetViewStore((state) => state.setTab);

  return (
    <div
      data-testid="budget-plan"
      className="flex-1 min-h-0 overflow-y-auto p-2 sm:p-3 flex flex-col gap-3 max-w-[820px] w-full mx-auto"
    >
      <div className={cn(SECTION_CLASS, "gap-2.5 p-4")}>
        <div className="flex items-center gap-3">
          <Typography level="title-md" className="flex-1">
            Coming in each month
          </Typography>
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className={SKELETON_PIECE_CLASS}
          >
            <BudgetViewPlanAmount
              key={budget.identifier}
              amount={budget.amountIncome}
              currency={currency}
              label="Coming in each month"
              testId="budget-plan-income"
              mutation={Mutations.CHANGE_BUDGET}
              input={(amount) => ({
                budgetIdentifier: budget.identifier,
                amountIncome: amount,
              })}
            />
          </Skeleton>
        </div>
        <Typography
          level="body-sm"
          data-testid="budget-plan-fits"
          className={cn(unplanned >= 0 && "text-muted-foreground")}
          style={unplanned < 0 ? { color: over.color } : undefined}
        >
          <Skeleton loading={isLoading} variant="inline">
            {`${money(committed)} to bills, saving and debts, ${money(everyday)} to everyday spending: ${
              unplanned < 0
                ? `${money(-unplanned)} more than comes in`
                : `${money(unplanned)} not planned for anything`
            }`}
          </Skeleton>
        </Typography>
      </div>
      {KINDS.filter((kind) => kind.value !== "SAVING").map((kind) => (
        <BudgetViewPlanSection
          key={kind.value}
          kind={kind}
          rows={rows.filter((row) => row.line.kind === kind.value)}
          budget={budget}
          currency={currency}
          isLoading={isLoading}
        />
      ))}
      {/* What is saved for is planned on the wishlist, alone. */}
      <section
        className={SECTION_CLASS}
        aria-label="Saving for"
        data-testid="budget-plan-section"
        data-kind="SAVING"
      >
        <div className="flex items-baseline gap-2">
          <Typography level="title-md">Saving for</Typography>
          <Typography
            level="body-xs"
            className="text-muted-foreground/80 flex-1"
          >
            what is put by each month for the wishlist
          </Typography>
          <Typography
            level="body-sm"
            className="tabular-nums text-muted-foreground"
          >
            <Skeleton loading={isLoading} variant="inline">
              {`${money(planned(["SAVING"]))} a month`}
            </Skeleton>
          </Typography>
        </div>
        <div>
          <Button
            variant="ghost"
            size="sm"
            data-testid="budget-plan-wishlist"
            onClick={() => setTab("wishlist")}
          >
            Open the wishlist
          </Button>
        </div>
      </section>
    </div>
  );
}

/** One part of the plan - its lines, each what it gets a month, and a way to add one. */
function BudgetViewPlanSection({
  kind,
  rows,
  budget,
  currency,
  isLoading,
}: {
  kind: (typeof KINDS)[number];
  rows: BudgetViewLineMonth[];
  budget: BudgetViewBudget;
  currency?: string;
  isLoading: boolean;
}) {
  const total = rows.reduce(
    (sum, row) => sum + Number(row.line.amountPlanned),
    0,
  );
  return (
    <section
      className={SECTION_CLASS}
      aria-label={kind.label}
      data-testid="budget-plan-section"
      data-kind={kind.value}
    >
      <div className="flex items-baseline gap-2">
        <Typography level="title-md">{kind.label}</Typography>
        <Typography level="body-xs" className="text-muted-foreground/80 flex-1">
          {kind.hint}
        </Typography>
        <Typography
          level="body-sm"
          className="tabular-nums text-muted-foreground"
        >
          <Skeleton loading={isLoading} variant="inline">
            {`${Utils.fCurrency(total, false, false, currency)} a month`}
          </Skeleton>
        </Typography>
      </div>
      {rows.map((row) => (
        <BudgetViewPlanLine key={row.line.id} row={row} currency={currency} />
      ))}
      {!isLoading && (
        <BudgetViewPlanAdd budget={budget} kind={kind.value} rows={rows} />
      )}
    </section>
  );
}

/** A line of the plan: its name, which part of the month it is, what it gets a month, and taking it out. */
function BudgetViewPlanLine({
  row,
  currency,
}: {
  row: BudgetViewLineMonth;
  currency?: string;
}) {
  const plan = useBackendMutation({
    mutation: Mutations.PLAN_BUDGET_LINE,
    refetchQueries: ["Budgets"],
  });
  const remove = useBackendMutation({
    mutation: Mutations.REMOVE_BUDGET_LINE,
    refetchQueries: ["Budgets"],
  });
  return (
    <div
      data-testid="budget-plan-line"
      data-line-id={row.line.identifier}
      className="flex items-center gap-2"
    >
      <Typography level="body-md" className="truncate flex-1 min-w-0">
        {nameBudgetLine(row.line)}
      </Typography>
      {row.line.chartedAccount && (
        <Select
          value={row.line.kind}
          onValueChange={(kind) =>
            kind &&
            kind !== row.line.kind &&
            plan.mutate({
              variables: {
                input: {
                  lineIdentifier: row.line.identifier,
                  amountPlanned: row.line.amountPlanned,
                  kind: kind as any,
                },
              },
            })
          }
        >
          <SelectTrigger
            size="sm"
            aria-label={`${nameBudgetLine(row.line)} is`}
            className="border-0 bg-transparent shadow-none dark:bg-transparent dark:hover:bg-transparent px-1 w-32"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {KINDS.map((kind) => (
              <SelectItem key={kind.value} value={kind.value}>
                {kind.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {(row.line.kind === "BILL" || row.line.kind === "EVERYDAY") && (
        <Select
          value={row.line.bucket}
          onValueChange={(bucket) =>
            bucket &&
            bucket !== row.line.bucket &&
            plan.mutate({
              variables: {
                input: {
                  lineIdentifier: row.line.identifier,
                  amountPlanned: row.line.amountPlanned,
                  bucket: bucket as any,
                },
              },
            })
          }
        >
          <SelectTrigger
            size="sm"
            aria-label={`${nameBudgetLine(row.line)} is a`}
            className="border-0 bg-transparent shadow-none dark:bg-transparent dark:hover:bg-transparent px-1 w-24"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="NEED">Need</SelectItem>
            <SelectItem value="WANT">Want</SelectItem>
          </SelectContent>
        </Select>
      )}
      {(row.line.kind === "BILL" || row.line.kind === "EVERYDAY") && (
        <Label className="flex items-center gap-2">
          <Checkbox
            checked={Boolean(row.line.isSpread)}
            onCheckedChange={(checked) =>
              plan.mutate({
                variables: {
                  input: {
                    lineIdentifier: row.line.identifier,
                    amountPlanned: row.line.amountPlanned,
                    isSpread: checked === true,
                  },
                },
              })
            }
            aria-label={`Spread ${nameBudgetLine(row.line)} over months`}
          />
          Spread
        </Label>
      )}
      <BudgetViewPlanAmount
        amount={row.line.amountPlanned}
        currency={currency}
        label={`${nameBudgetLine(row.line)} a month`}
        testId="budget-plan-amount"
        mutation={Mutations.PLAN_BUDGET_LINE}
        input={(amount) => ({
          lineIdentifier: row.line.identifier,
          amountPlanned: amount,
        })}
      />
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={`Take ${nameBudgetLine(row.line)} out of the plan`}
        onClick={() =>
          remove.mutate({
            variables: { input: { lineIdentifier: row.line.identifier } },
          })
        }
      >
        <Icon ReactIcon={XIcon} size="xs" />
      </Button>
    </div>
  );
}

/**
 * An amount of the plan, typed: written when it is left or Enter is pressed,
 * and only where it changed.
 */
export function BudgetViewPlanAmount({
  amount,
  currency,
  label,
  testId,
  mutation,
  input,
  month,
}: {
  amount: string;
  /** What it is in, said as every amount of the view says it: none for its business's own. */
  currency?: string;
  label: string;
  testId: string;
  mutation:
    | typeof Mutations.PLAN_BUDGET_LINE
    | typeof Mutations.CHANGE_BUDGET
    | typeof Mutations.SET_BUDGET_GOAL;
  input: (amount: string) => Record<string, unknown>;
  /** The month the view reads: what is written brings it back worked out again, in place of reading it again. */
  month?: string;
}) {
  const [typed, setTyped] = useState(String(Number(amount)));
  useEffect(() => setTyped(String(Number(amount))), [amount]);
  const { mutate } = useBackendMutation({
    mutation: mutation as typeof Mutations.PLAN_BUDGET_LINE,
    refetchQueries: month
      ? ["BudgetLineMonth"]
      : ["Budgets", "BudgetLineMonth"],
  });
  const save = () => {
    const value = typed.replace(/[^0-9.]/g, "");
    if (value === "" || Number(value) === Number(amount)) {
      setTyped(String(Number(amount)));
      return;
    }
    mutate({ variables: { input: input(value) as any, month } });
  };
  return (
    <InputGroup className="h-8">
      <InputGroupAddon>
        {Utils.fCurrency(0, false, false, currency).replace("0", "")}
      </InputGroupAddon>
      <InputGroupInput
        value={typed}
        inputMode="decimal"
        aria-label={label}
        onChange={(event) => setTyped(event.target.value)}
        onBlur={save}
        onKeyDown={(event) => {
          if (event.key === "Enter") (event.target as HTMLInputElement).blur();
        }}
        data-testid={testId}
        style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}
      />
    </InputGroup>
  );
}

/**
 * Adding to a part of the plan: a goal by its name, for saving; an account of
 * the chart not planned yet, for the rest.
 */
function BudgetViewPlanAdd({
  budget,
  kind,
  rows,
}: {
  budget: BudgetViewBudget;
  kind: string;
  rows: BudgetViewLineMonth[];
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [goal, setGoal] = useState("");
  const { data } = useBackendQuery(Queries.BUDGET_PLAN_ACCOUNTS_QUERY, {
    variables: { entityIdentifiers: [budget.entity.identifier] },
    skip: !isAdding || kind === "SAVING",
    fetchPolicy: "cache-first",
  });
  const planned = new Set(
    budget.month.lines.map((row) => row.line.chartedAccount?.id),
  );
  // What is spent on: the chart's expenses, or - for a debt - a card or loan, by what each account is, whatever the
  // chart calls it.
  const offered = (data?.chartedAccounts?.data ?? []).filter(
    (account) =>
      !planned.has(account.id) &&
      account.type === (kind === "DEBT" ? "LIABILITIES" : "EXPENSES"),
  );
  const add = useBackendMutation({
    mutation: Mutations.ADD_BUDGET_LINE,
    refetchQueries: ["Budgets"],
    callbacks: { onSuccess: () => setIsAdding(false) },
  });
  const addGoal = useBackendMutation({
    mutation: Mutations.ADD_BUDGET_GOAL,
    refetchQueries: ["Budgets"],
    callbacks: {
      onSuccess: () => {
        setGoal("");
        setIsAdding(false);
      },
    },
  });

  if (!isAdding) {
    return (
      <div>
        <Button
          variant="ghost"
          size="sm"
          data-testid="budget-plan-add"
          onClick={() => setIsAdding(true)}
        >
          <Icon ReactIcon={PlusIcon} size="xs" />
          {rows.length ? "Add another" : "Add one"}
        </Button>
      </div>
    );
  }
  if (kind === "SAVING") {
    return (
      <Input
        autoFocus
        placeholder="What for: Emergency fund, Vacation"
        value={goal}
        onChange={(event) => setGoal(event.target.value)}
        onBlur={() => !goal.trim() && setIsAdding(false)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && goal.trim()) {
            addGoal.mutate({
              variables: {
                input: {
                  budgetIdentifier: budget.identifier,
                  name: goal.trim(),
                },
              },
            });
          }
          if (event.key === "Escape") setIsAdding(false);
        }}
        data-testid="budget-plan-goal"
        className="h-8"
      />
    );
  }
  return (
    <BudgetViewPlanAccountPicker
      offered={offered}
      onClose={() => setIsAdding(false)}
      onPick={(account) =>
        add.mutate({
          variables: {
            input: {
              budgetIdentifier: budget.identifier,
              chartedAccountIdentifier: account.identifier,
              kind: kind as any,
            },
          },
        })
      }
    />
  );
}

type BudgetViewPlanAccount = {
  id: string;
  identifier: string;
  name?: string | null;
  fullPath?: (string | null)[] | null;
};

/**
 * An account of the chart to plan, picked by typing part of its name: the
 * accounts whose names have what is typed in them, under the accounts each
 * is in, open below it from the start - Down and Up move through them, Enter
 * picks the one lit, Escape and leaving shut it.
 */
function BudgetViewPlanAccountPicker({
  offered,
  onPick,
  onClose,
}: {
  offered: BudgetViewPlanAccount[];
  onPick: (account: BudgetViewPlanAccount) => void;
  onClose: () => void;
}) {
  const listboxId = useId();
  const [typed, setTyped] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const shown = offered.filter((account) =>
    (account.name ?? "").toLowerCase().includes(typed.trim().toLowerCase()),
  );
  // Under the account each is in, in the order the chart gives them.
  const groups = shown.reduce<Map<string, BudgetViewPlanAccount[]>>(
    (byGroup, account) => {
      const group = (account.fullPath ?? []).slice(0, -1).join(" / ");
      return byGroup.set(group, [...(byGroup.get(group) ?? []), account]);
    },
    new Map(),
  );
  const ordered = [...groups.values()].flat();
  const lit = ordered[Math.min(highlighted, ordered.length - 1)];
  return (
    // A combobox: the field keeps the keyboard, the list under it is the
    // Popover's, opened with the field and taking no focus of its own.
    <Popover open={ordered.length > 0}>
      <PopoverAnchor asChild>
        <Input
          autoFocus
          placeholder="An account of your chart"
          value={typed}
          onChange={(event) => {
            setTyped(event.target.value);
            setHighlighted(0);
          }}
          onBlur={onClose}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              const by = event.key === "ArrowDown" ? 1 : -1;
              setHighlighted(
                (Math.min(highlighted, ordered.length - 1) +
                  by +
                  ordered.length) %
                  Math.max(ordered.length, 1),
              );
            }
            if (event.key === "Enter" && lit) onPick(lit);
            if (event.key === "Escape") onClose();
          }}
          data-testid="budget-plan-account"
          role="combobox"
          aria-expanded={true}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            lit ? `${listboxId}-${lit.identifier}` : undefined
          }
          className="h-8"
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(event) => event.preventDefault()}
        // Its options pressed without taking the caret from what is typed.
        onMouseDown={(event) => event.preventDefault()}
        className="w-(--radix-popover-trigger-width) max-h-72 overflow-y-auto p-1 text-xs"
      >
        <ul id={listboxId} role="listbox" data-select-listbox="">
          {[...groups.entries()].map(([group, accounts]) => (
            <li key={group} role="presentation">
              {group && (
                <Typography
                  component="div"
                  level="body-xs"
                  className="sticky top-0 z-[1] px-2 py-1.5 uppercase tracking-[0.1em] font-semibold text-muted-foreground/80 bg-popover"
                >
                  {group}
                </Typography>
              )}
              <ul role="group">
                {accounts.map((account) => (
                  <li
                    key={account.id}
                    id={`${listboxId}-${account.identifier}`}
                    role="option"
                    aria-selected={account === lit}
                    data-highlighted={account === lit || undefined}
                    className="cursor-default rounded-sm px-2 py-1.5 hover:bg-accent hover:text-accent-foreground data-highlighted:bg-accent data-highlighted:text-accent-foreground"
                    onClick={() => onPick(account)}
                  >
                    {account.name}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
